import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Code2, ExternalLink, History, LayoutTemplate, Upload } from "lucide-react";
import { api, LandingPreviewPage, LandingSectionEditPatch, LandingVersionItem } from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import {
  iframeUrl,
  mergeAuditsWithStructure,
  type SectionAudit,
  type VerdictId,
} from "@/lib/landingSectionAudit";
import LandingHtmlEditorModal from "@/components/landing/LandingHtmlEditorModal";
import SectionVariantsPanel from "@/components/landing/SectionVariantsPanel";
import VoiceResearchChat from "@/components/voice/VoiceResearchChat";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type PreviewMode = "server" | "live";

function serverPreviewUrl(pageId: string, sectionId: string | null): string {
  const qs = new URLSearchParams({ page_id: pageId });
  const base = `/api/landing-preview/preview-html?${qs.toString()}`;
  if (!sectionId) return base;
  return `${base}#${encodeURIComponent(sectionId)}`;
}

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

const VERDICT_STYLE: Record<
  VerdictId,
  { label: string; badge: string; card: string; cardActive: string }
> = {
  ok: {
    label: "OK",
    badge: "bg-emerald-700 text-white",
    card: "border-emerald-400 bg-emerald-100 hover:bg-emerald-200/90",
    cardActive: "border-emerald-600 bg-emerald-200 ring-2 ring-emerald-500/50",
  },
  uwaga: {
    label: "Uwaga",
    badge: "bg-amber-700 text-white",
    card: "border-amber-400 bg-amber-100 hover:bg-amber-200/90",
    cardActive: "border-amber-600 bg-amber-200 ring-2 ring-amber-500/50",
  },
  do_poprawy: {
    label: "Do poprawy",
    badge: "bg-rose-700 text-white",
    card: "border-rose-400 bg-rose-100 hover:bg-rose-200/90",
    cardActive: "border-rose-600 bg-rose-200 ring-2 ring-rose-500/50",
  },
  brak: {
    label: "Brak sygnału",
    badge: "bg-slate-600 text-white",
    card: "border-slate-300 bg-slate-100 hover:bg-slate-200/90",
    cardActive: "border-slate-500 bg-slate-200 ring-2 ring-slate-400/50",
  },
  poza_strona: {
    label: "Poza stroną",
    badge: "bg-slate-500 text-white",
    card: "border-slate-300/80 bg-slate-100/70 opacity-55 hover:opacity-70",
    cardActive: "border-slate-400 bg-slate-200/80 opacity-70",
  },
};

function SectionCard({
  row,
  indexLabel,
  selected,
  onSelect,
}: {
  row: SectionAudit;
  indexLabel: number;
  selected: boolean;
  onSelect: () => void;
}) {
  const v = VERDICT_STYLE[row.verdict];
  const s = row.section;
  const clicks = s.cta_clicks ?? 0;
  const topCta = (s.ctas ?? [])[0];
  const clickable = row.onPage;

  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={() => {
        if (clickable) onSelect();
      }}
      className={cn(
        "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
        selected ? v.cardActive : v.card,
        !clickable && "cursor-default",
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            <span className="mr-1 tabular-nums text-muted-foreground">
              {indexLabel}.
            </span>
            {row.label}
          </div>
          <div className="truncate font-mono text-[10px] text-muted-foreground">
            #{s.section_id}
          </div>
        </div>
        <span
          className={cn(
            "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
            v.badge,
          )}
        >
          {v.label}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
        <span title={`${s.sessions_reached} wizyt`}>
          dojście {pct(s.view_rate)}
        </span>
        {row.dropPp != null && row.dropPp > 0.5 && (
          <span>drop −{Math.round(row.dropPp)} pp</span>
        )}
        <span className={clicks > 0 ? "text-foreground/80" : undefined}>
          CTA{" "}
          {clicks > 0
            ? `${clicks} · ${pct(s.cta_rate_of_viewers)} widzów`
            : "–"}
        </span>
      </div>
      {topCta ? (
        <div className="mt-1 truncate text-[10px] text-muted-foreground">
          {topCta.label} · {topCta.clicks}
        </div>
      ) : null}
      <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
        {row.reason}
      </p>
    </button>
  );
}

export default function Landingi() {
  const qc = useQueryClient();
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [pageId, setPageId] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("server");
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [pendingEdits, setPendingEdits] = useState<LandingSectionEditPatch[]>([]);
  const [diffSrcDoc, setDiffSrcDoc] = useState<string | null>(null);
  const [diffError, setDiffError] = useState<string | null>(null);

  const catalogQuery = useQuery({
    queryKey: ["landing-preview-catalog"],
    queryFn: api.landingPreviewCatalog,
    retry: false,
  });

  const pages = catalogQuery.data?.pages ?? [];
  const activePage: LandingPreviewPage | undefined =
    pages.find((p) => p.page_id === pageId) || pages[0];

  const effectivePageId = activePage?.page_id ?? "";

  const journeyQuery = useQuery({
    queryKey: ["landing-preview-journey", effectivePageId, period],
    queryFn: () =>
      api.siteAnalyticsJourney({
        ...journeyWindowParams(period),
        page_id: effectivePageId,
      }),
    enabled: Boolean(effectivePageId),
    retry: false,
  });

  const structureQuery = useQuery({
    queryKey: ["landing-preview-structure", effectivePageId],
    queryFn: () => api.landingPreviewStructure(effectivePageId),
    enabled: Boolean(effectivePageId),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const pageColumn = useMemo(() => {
    const cols = journeyQuery.data?.sections_by_page?.pages ?? [];
    return (
      cols.find((c) => c.page_id === effectivePageId) || cols[0] || null
    );
  }, [journeyQuery.data, effectivePageId]);

  const { onPage, offPage, structureOk } = useMemo(
    () =>
      mergeAuditsWithStructure(
        pageColumn?.sections ?? [],
        catalogQuery.data?.section_labels,
        structureQuery.data
          ? {
              ok: Boolean(structureQuery.data.ok),
              section_ids: structureQuery.data.section_ids ?? [],
            }
          : null,
      ),
    [pageColumn, catalogQuery.data?.section_labels, structureQuery.data],
  );

  const issueCount = onPage.filter(
    (a) => a.verdict === "do_poprawy" || a.verdict === "uwaga",
  ).length;

  const previewSrc = activePage
    ? previewMode === "server" && activePage.editable !== false && activePage.source_rel
      ? serverPreviewUrl(activePage.page_id, selectedSection)
      : iframeUrl(activePage.live_url, selectedSection)
    : "";

  const canEditSource = Boolean(activePage?.source_rel);

  const versionsQ = useQuery({
    queryKey: ["landing-preview-versions", effectivePageId],
    queryFn: () => api.landingPreviewVersions(effectivePageId),
    enabled: Boolean(effectivePageId) && versionsOpen && canEditSource,
    retry: false,
  });

  const bumpPreview = (note?: string) => {
    setPreviewMode("server");
    setIframeKey((k) => k + 1);
    if (note) setStatusNote(note);
    void qc.invalidateQueries({ queryKey: ["landing-preview-versions", effectivePageId] });
  };

  // Wejście / zmiana strony: lokalny HTML = live; niezapisane giną; historia wersji zostaje.
  useEffect(() => {
    if (!effectivePageId || !canEditSource) return;
    let cancelled = false;
    setPendingEdits([]);
    setDiffSrcDoc(null);
    setDiffError(null);
    setEditorOpen(false);
    setStatusNote("Wczytywanie z live…");
    api
      .landingPreviewPullLive({ page_id: effectivePageId })
      .then((res) => {
        if (cancelled) return;
        setPreviewMode("server");
        setIframeKey((k) => k + 1);
        setStatusNote(res.note_pl || "Wczytano z live.");
        void qc.invalidateQueries({
          queryKey: ["landing-preview-structure", effectivePageId],
        });
        void qc.invalidateQueries({
          queryKey: ["landing-preview-versions", effectivePageId],
        });
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setStatusNote(
          err?.message
            ? `Nie udało się wczytać z live: ${err.message}`
            : "Nie udało się wczytać z live.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [effectivePageId, canEditSource, qc]);

  const showPendingDiff =
    previewMode === "server" &&
    pendingEdits.length > 0 &&
    Boolean(effectivePageId && selectedSection);

  useEffect(() => {
    if (!showPendingDiff || !selectedSection) {
      setDiffSrcDoc(null);
      setDiffError(null);
      return;
    }
    let cancelled = false;
    setDiffError(null);
    api
      .landingPreviewDiffHtml({
        page_id: effectivePageId,
        section_id: selectedSection,
        patches: pendingEdits,
      })
      .then((html) => {
        if (!cancelled) setDiffSrcDoc(html);
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setDiffSrcDoc(null);
          setDiffError(err?.message || "Nie udało się zbudować podglądu zmian");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [showPendingDiff, effectivePageId, selectedSection, pendingEdits, iframeKey]);

  const publishM = useMutation({
    mutationFn: () => api.landingPreviewPublish({ page_id: effectivePageId }),
    onSuccess: (res) => {
      setStatusNote(res.note_pl || "Wgrano na live.");
      setIframeKey((k) => k + 1);
    },
  });

  const restoreM = useMutation({
    mutationFn: (versionId: string) =>
      api.landingPreviewVersionRestore({
        page_id: effectivePageId,
        version_id: versionId,
      }),
    onSuccess: (res) => {
      bumpPreview(res.note_pl || "Przywrócono wersję.");
      setVersionsOpen(false);
    },
  });

  const pageContext = useMemo(() => {
    if (!activePage) return null;
    const periodOpt = JOURNEY_WINDOW_OPTIONS.find((o) => o.id === period);
    return {
      page_id: activePage.page_id,
      label: activePage.label,
      live_url: activePage.live_url,
      period: periodOpt?.label || period,
      selected_section: selectedSection || "",
      sections: onPage.slice(0, 24).map((row) => ({
        section_id: row.section.section_id,
        label: row.label,
        verdict: row.verdict,
        reason: row.reason,
        view_rate: row.section.view_rate,
        cta_clicks: row.section.cta_clicks,
      })),
    };
  }, [activePage, period, selectedSection, onPage]);

  const metricsDays = useMemo(() => {
    if (period === "today" || period === "yesterday") return 1;
    return Number(period) || 7;
  }, [period]);

  const selectedSectionLabel = useMemo(() => {
    if (!selectedSection) return "";
    return (
      onPage.find((r) => r.section.section_id === selectedSection)?.label ||
      selectedSection
    );
  }, [onPage, selectedSection]);

  const onSelectPage = (id: string) => {
    setPageId(id);
    setSelectedSection(null);
    setPendingEdits([]);
    setIframeKey((k) => k + 1);
  };

  const onSelectSection = (sectionId: string) => {
    setSelectedSection(sectionId);
    setIframeKey((k) => k + 1);
  };

  return (
    <div className="flex h-[calc(100dvh-5.75rem)] flex-col overflow-hidden md:h-[calc(100dvh-4rem)]">
      <header className="mb-3 flex shrink-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <h1 className="text-xl font-bold tracking-tight">Landingi</h1>
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
            {pages.map((p) => (
              <Button
                key={p.page_id}
                size="sm"
                className="h-7 px-2.5 text-xs"
                variant={p.page_id === effectivePageId ? "default" : "outline"}
                onClick={() => onSelectPage(p.page_id)}
              >
                {p.label}
              </Button>
            ))}
            {catalogQuery.isLoading && (
              <span className="text-xs text-muted-foreground">Katalog…</span>
            )}
          </div>
          {activePage ? (
            <Button variant="outline" size="sm" className="h-7 shrink-0 px-2.5 text-xs" asChild>
              <a href={activePage.live_url} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                Live
              </a>
            </Button>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {JOURNEY_WINDOW_OPTIONS.map((opt) => (
            <Button
              key={opt.id}
              size="sm"
              className="h-7 px-2.5 text-xs"
              variant={opt.id === period ? "default" : "outline"}
              onClick={() => setPeriod(opt.id)}
            >
              {opt.label}
            </Button>
          ))}
          <span className="ml-1 text-[11px] text-muted-foreground">
            {journeyQuery.data?.period?.label_pl ?? "czas PL"}
            {pageColumn?.sessions != null ? ` · ${pageColumn.sessions} wizyt` : ""}
            {structureOk
              ? ` · ${onPage.length} na stronie · ${offPage.length} poza`
              : ""}
            {issueCount > 0 ? ` · ${issueCount} do przeglądu` : ""}
          </span>
          <InfoTip tipId="section_reach" className="ml-0.5 inline-flex align-middle" />
          <InfoTip tipId="section_cta" className="inline-flex align-middle" />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden lg:flex-row">
        <div className="grid min-h-0 min-w-0 flex-1 gap-4 overflow-hidden max-lg:auto-rows-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1.4fr)_minmax(200px,0.45fr)]">
          <Card className="flex h-full min-h-0 flex-col overflow-hidden max-lg:min-h-[42vh]">
            <CardHeader className="shrink-0 space-y-2 border-b py-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
                  Podgląd
                </CardTitle>
                <div className="flex flex-wrap items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    variant={previewMode === "server" ? "default" : "outline"}
                    disabled={!canEditSource}
                    onClick={() => {
                      setPreviewMode("server");
                      setIframeKey((k) => k + 1);
                    }}
                  >
                    Serwer
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    variant={previewMode === "live" ? "default" : "outline"}
                    onClick={() => {
                      setPreviewMode("live");
                      setIframeKey((k) => k + 1);
                    }}
                  >
                    Live
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    variant="outline"
                    disabled={!canEditSource}
                    onClick={() => setEditorOpen(true)}
                  >
                    <Code2 className="mr-1 h-3.5 w-3.5" />
                    Edytuj HTML
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    variant="outline"
                    disabled={!canEditSource}
                    onClick={() => setVersionsOpen((v) => !v)}
                  >
                    <History className="mr-1 h-3.5 w-3.5" />
                    Wersje
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    variant="outline"
                    disabled={!canEditSource || publishM.isPending}
                    onClick={() => publishM.mutate()}
                  >
                    <Upload className="mr-1 h-3.5 w-3.5" />
                    {publishM.isPending ? "Wgrywam…" : "Wgraj live"}
                  </Button>
                </div>
              </div>
              {activePage && (
                <p className="truncate font-mono text-[11px] text-muted-foreground">
                  {previewMode === "server" ? "serwer panelu" : activePage.live_url}
                  {selectedSection ? `#${selectedSection}` : ""}
                  {showPendingDiff ? " · podgląd zmian (niezapisane)" : ""}
                </p>
              )}
              {diffError ? (
                <p className="text-[11px] text-rose-700">{diffError}</p>
              ) : null}
              {statusNote ? (
                <p className="rounded-md bg-slate-50 px-2 py-1 text-[11px] text-slate-700 ring-1 ring-slate-200">
                  {statusNote}
                </p>
              ) : null}
              {publishM.isError ? (
                <p className="text-[11px] text-rose-700">
                  {(publishM.error as Error)?.message || "Publish nieudany"}
                </p>
              ) : null}
              {versionsOpen && canEditSource ? (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-white p-2">
                  {versionsQ.isLoading ? (
                    <p className="text-[11px] text-muted-foreground">Wczytywanie wersji…</p>
                  ) : null}
                  {(versionsQ.data?.items || []).length === 0 && !versionsQ.isLoading ? (
                    <p className="text-[11px] text-muted-foreground">Brak zapisanych wersji.</p>
                  ) : null}
                  {(versionsQ.data?.items || []).map((v: LandingVersionItem) => (
                    <div
                      key={v.id}
                      className="flex items-center justify-between gap-2 text-[11px]"
                    >
                      <span className="min-w-0 truncate">
                        {v.created_at} · {v.kind}
                        {v.note ? ` · ${v.note}` : ""}
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        className="h-6 shrink-0 px-2 text-[10px]"
                        variant="outline"
                        disabled={restoreM.isPending}
                        onClick={() => restoreM.mutate(v.id)}
                      >
                        Przywróć
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}
            </CardHeader>
            <CardContent className="relative min-h-0 flex-1 p-0">
              {!activePage && (
                <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                  Brak katalogu landingów.
                </div>
              )}
              {activePage && showPendingDiff && diffSrcDoc ? (
                <iframe
                  key={`diff-${iframeKey}`}
                  title={`Podgląd zmian ${activePage.label}`}
                  srcDoc={diffSrcDoc}
                  className="absolute inset-0 h-full w-full border-0 bg-background"
                  sandbox="allow-same-origin"
                />
              ) : null}
              {activePage && !(showPendingDiff && diffSrcDoc) ? (
                <iframe
                  key={`${iframeKey}-${previewSrc}`}
                  title={`Podgląd ${activePage.label}`}
                  src={previewSrc}
                  className="absolute inset-0 h-full w-full border-0 bg-background"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              ) : null}
            </CardContent>
          </Card>

          <Card className="flex h-full min-h-0 flex-col overflow-hidden max-lg:min-h-[36vh]">
            <CardHeader className="shrink-0 border-b py-3">
              <CardTitle className="text-base">Sekcje i metryki</CardTitle>
              <p className="text-xs text-muted-foreground">
                Werdykt tylko dla sekcji na stronie. Klik = fokus chatu + scroll.
              </p>
              {structureQuery.isError ||
              (structureQuery.data && !structureQuery.data.ok) ? (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
                  Nie udało się odczytać struktury live
                  {structureQuery.data?.error
                    ? ` (${structureQuery.data.error})`
                    : ""}
                  . Pokazuję pełną listę journey bez oznaczania „poza stroną”.
                </p>
              ) : null}
            </CardHeader>
            <CardContent className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-3">
              {journeyQuery.isLoading && (
                <p className="py-6 text-sm text-muted-foreground">
                  Wczytywanie metryk…
                </p>
              )}
              {journeyQuery.isError && (
                <p className="py-6 text-sm text-muted-foreground">
                  Nie udało się wczytać journey.
                </p>
              )}
              {journeyQuery.data &&
                onPage.length === 0 &&
                offPage.length === 0 && (
                  <p className="py-6 text-sm text-muted-foreground">
                    Brak danych o sekcjach dla tej strony w wybranym okresie.
                  </p>
                )}

              {onPage.length > 0 && structureOk && (
                <p className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Na stronie
                </p>
              )}
              {onPage.map((row, i) => (
                <SectionCard
                  key={`on-${row.section.section_id}`}
                  row={row}
                  indexLabel={i + 1}
                  selected={selectedSection === row.section.section_id}
                  onSelect={() => onSelectSection(row.section.section_id)}
                />
              ))}

              {offPage.length > 0 && (
                <>
                  <p className="pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Poza stroną (stara wersja / poza trackerem)
                  </p>
                  {offPage.map((row, i) => (
                    <SectionCard
                      key={`off-${row.section.section_id}`}
                      row={row}
                      indexLabel={onPage.length + i + 1}
                      selected={false}
                      onSelect={() => undefined}
                    />
                  ))}
                </>
              )}

              {effectivePageId ? (
                <SectionVariantsPanel
                  pageId={effectivePageId}
                  sectionId={selectedSection}
                  sectionLabel={selectedSectionLabel}
                  days={metricsDays}
                />
              ) : null}
            </CardContent>
          </Card>
        </div>

        <VoiceResearchChat
          surface="landings_chat"
          pageContext={pageContext}
          pendingEdits={pendingEdits}
          onPendingEditsChange={setPendingEdits}
          onLocalHtmlApplied={(note) =>
            bumpPreview(note || "Zapisano na serwerze panelu. Podgląd odświeżony. Live: użyj Wgraj live.")
          }
          className="h-full min-h-0 flex-1 overflow-hidden rounded-xl border border-slate-200 max-lg:min-h-0 lg:w-[720px] lg:min-w-[640px] lg:max-w-[800px] lg:flex-none lg:shrink-0"
        />
      </div>

      <LandingHtmlEditorModal
        open={editorOpen}
        pageId={effectivePageId}
        sectionId={selectedSection}
        sourceRel={activePage?.source_rel}
        pendingEdits={pendingEdits}
        onClose={() => setEditorOpen(false)}
        onClearPending={() => setPendingEdits([])}
        onSaved={(note) => bumpPreview(note)}
      />
    </div>
  );
}
